// Spec & handoff (Blue Market only): readiness, Claude runs for the spec and the handoff package, the spec page, and
// change requests after the spec (drafted from a later round, approved, applied to the spec, packaged for the build).
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { Modal, ago, formatDateTime, useConfirm, useToast } from '../ui';
import type { ChangeDoc, ChangeRequest, ChangeStatus, ClaudeRun, Changes, Project, ScreenDiff, SpecDoc, SpecHandoff } from '../types';
import { Page, useCrumbs } from './Shell';

const KIND_LABEL: Record<ClaudeRun['kind'], string> = { spec: 'Spec', handoff: 'Handoff', 'design-review': 'Next round', kit: 'UI kit' };
const STATUS: Record<ClaudeRun['status'], { label: string; badge: string }> = {
  queued: { label: 'Waiting to start', badge: 'warn' }, running: { label: 'Claude is working', badge: 'info' },
  ok: { label: 'Finished', badge: 'ok' }, failed: { label: 'Didn\'t finish', badge: 'bad' },
};
const active = (r: ClaudeRun | null | undefined) => r?.status === 'queued' || r?.status === 'running';
const CHANGE_STATUS: Record<ChangeStatus, { label: string; badge: string }> = {
  draft: { label: 'Draft', badge: '' }, approved: { label: 'Approved', badge: 'info' }, rejected: { label: 'Rejected', badge: 'bad' }, applied: { label: 'In the spec', badge: 'ok' },
};

function runTitle(run: ClaudeRun) {
  const c = run.change;
  if (c) return c.step === 'draft' ? `Change request CR-${c.number} · v${c.from} → v${run.round}` : c.step === 'apply' ? `Apply CR-${c.number} to the spec · v${run.round}` : `Package CR-${c.number}`;
  if (run.kind === 'design-review' && run.round === 0) return 'First round · from the brief → v1';
  return `${KIND_LABEL[run.kind]}${run.round && run.kind !== 'kit' ? (run.kind === 'design-review' ? ` · v${run.round} → v${run.round + 1}` : ` · v${run.round}`) : ''}`;
}

function useSpecHandoff(projectId: string) {
  return useQuery({ queryKey: ['spec-handoff', projectId], queryFn: () => get<SpecHandoff>(`/api/projects/${projectId}/spec-handoff`) });
}

// ---------- the latest Claude run ----------

export function ClaudeRunPanel({ run, canStop }: { run: ClaudeRun; canStop: boolean }) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [open, setOpen] = useState(active(run));
  const logRef = useRef<HTMLPreElement>(null);
  const running = active(run);
  useEffect(() => { if (running) setOpen(true); }, [running]);
  useEffect(() => { const el = logRef.current; if (el) el.scrollTop = el.scrollHeight; }, [run.log, open]);
  const stop = async () => {
    if (!await confirm('Stop Claude?', { body: 'Whatever it has already changed stays. You can start a new run afterwards.', ok: 'Stop', danger: true })) return;
    try { await post(`/api/jobs/${run.id}/cancel`); } catch (e) { toast((e as Error).message, true); }
  };
  const s = STATUS[run.status];
  return (
    <div className={`claude-run ${running ? 'running' : ''}`}>
      <div className="row wrap" style={{ gap: 8 }}>
        {running && <span className="spin" />}
        <b className="small">{runTitle(run)}</b>
        <span className={`badge ${s.badge}`}>{s.label}</span>
        <span className="muted tiny" title={formatDateTime(run.created_at)}>
          {run.finished_at ? `finished ${ago(run.finished_at)}` : run.started_at ? `started ${ago(run.started_at)}` : `queued ${ago(run.created_at)}`}
        </span>
        <span className="grow" />
        <button className="btn sm ghost" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? 'Hide progress' : 'Show progress'}</button>
        {running && canStop && <button className="btn sm danger" onClick={stop}>Stop</button>}
      </div>
      {run.instructions && <p className="muted tiny" style={{ margin: '6px 0 0' }}>Instructions: {run.instructions}</p>}
      {open && <pre className="run-log" ref={logRef}>{run.log || 'Waiting for the Claude worker…'}</pre>}
      {run.summary_html && <div className="prose run-summary" dangerouslySetInnerHTML={{ __html: run.summary_html }} />}
      {dialog}
    </div>
  );
}

// ---------- the section on a project page ----------

function Check({ ok, title, children }: { ok: boolean; title: string; children?: React.ReactNode }) {
  return (
    <div className="check-row">
      <span className={`check-mark ${ok ? 'ok' : 'warn'}`} aria-hidden="true">{ok ? '✓' : '!'}</span>
      <div className="grow" style={{ minWidth: 0 }}><div className="small"><b>{title}</b></div>{children}</div>
    </div>
  );
}

export function SpecHandoffSection({ project }: { project: Project }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useSpecHandoff(project.id);
  const [asking, setAsking] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const canRun = can('claude.run') && can('delivery.edit');
  const refresh = () => qc.invalidateQueries({ queryKey: ['spec-handoff', project.id] });
  const handoff = useMutation({ mutationFn: () => post<ClaudeRun>(`/api/projects/${project.id}/handoff/claude`), onSuccess: refresh, onError: (e: Error) => toast(e.message, true) });
  if (!data) return null;
  const busy = active(data.run);
  const open = data.questions.filter((q) => !q.settled);
  const tbd = Object.entries(data.stack).filter(([, v]) => !v || v.toLowerCase() === 'tbd');

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head"><h2>Spec & handoff</h2><span className="muted tiny mono">{project.path}/spec</span></div>
      <div className="stack" style={{ padding: '14px 18px', gap: 12 }}>
        <Check ok={data.round?.visibility === 'approved'} title={data.round ? `Designs: v${data.round.number}${data.round.visibility === 'approved' ? ' (approved)' : ''}` : 'Designs: no built round yet'}>
          <div className="muted tiny">{!data.round ? 'Build a design round first.' : data.round.visibility === 'approved'
            ? 'The spec describes the latest approved round.' : 'Not approved yet. The spec will describe this round, the latest one built.'}</div>
        </Check>
        <Check ok={open.length === 0} title={open.length ? `${open.length} open question${open.length === 1 ? '' : 's'}` : data.questions.length ? 'Open questions: all settled' : 'No open questions'}>
          <div className="muted tiny">{open.length ? 'Claude assumes an answer for each and lists them in the spec, so the client can confirm. ' : ''}
            Answers recorded in decisions.md are followed.
            {data.questions.length > 0 && <> <button className="link-btn" onClick={() => setShowQuestions(!showQuestions)}>{showQuestions ? 'Hide' : 'Show'} questions</button></>}
            {' · '}<Link to={`/projects/${project.id}?tab=questions`}>{open.length ? 'Answer them' : 'Open questions'}</Link>
          </div>
          {showQuestions && (
            <ul className="question-list">
              {data.questions.map((q) => (
                <li key={q.key}>
                  <b>{q.topic}</b>{q.text !== q.topic && <>: {q.text}</>}
                  {q.settled ? <span className="badge ok tiny-badge">{q.settled === 'answer' ? 'Answered' : 'Assumption agreed'}</span>
                    : q.assumed ? <div className="muted tiny">Assumed in the spec: {q.assumed}</div> : null}
                  {q.source === 'spec' && !q.settled && <span className="badge tiny-badge">Raised in the spec</span>}
                </li>
              ))}
            </ul>
          )}
        </Check>
        <Check ok={tbd.length === 0} title="Stack">
          {Object.keys(data.stack).length > 0 ? (
            <div className="row wrap tiny" style={{ gap: '4px 12px', marginTop: 2 }}>
              {Object.entries(data.stack).map(([k, v]) => <span key={k}><span className="muted">{k}</span> {v || 'tbd'}</span>)}
            </div>
          ) : <div className="muted tiny">Not set in project.json.</div>}
          {tbd.length > 0 && <div className="muted tiny">Claude proposes the parts still "tbd" and records them in decisions.md to confirm.</div>}
        </Check>
      </div>

      <div className="round-row">
        <div className="grow" style={{ minWidth: 180 }}>
          <div className="row wrap" style={{ gap: 6 }}>
            <b className="small">Tech spec</b>
            {!data.spec.exists && <span className="badge">Not written yet</span>}
            {data.spec.older_than_designs && <span className="badge warn" title="The round's designs changed after the spec was written">Older than the designs</span>}
            {data.spec.decisions_changed && <span className="badge warn" title="decisions.md changed after the spec was written">New decisions since</span>}
          </div>
          <div className="muted tiny" style={{ marginTop: 4 }}>{data.spec.updated_at ? `spec/tech-spec.md · updated ${ago(data.spec.updated_at)}` : 'Claude writes spec/tech-spec.md from the designs, notes and decisions.'}</div>
        </div>
        {data.spec.exists && <Link className="btn sm" to={`/projects/${project.id}/spec`}>Open</Link>}
        {canRun && <button className="btn sm" disabled={busy || !data.round} onClick={() => setAsking(true)}>{data.spec.exists ? 'Revise with Claude' : 'Write with Claude'}</button>}
      </div>
      <div className="round-row">
        <div className="grow" style={{ minWidth: 180 }}>
          <div className="row wrap" style={{ gap: 6 }}>
            <b className="small">Handoff package</b>
            {data.handoff?.older_than_spec && <span className="badge warn">Older than the spec</span>}
          </div>
          <div className="muted tiny" style={{ marginTop: 4 }}>
            {data.handoff ? `${data.handoff.name} · v${data.handoff.round} · ${(data.handoff.size / 1048576).toFixed(1)} MB · ${ago(data.handoff.updated_at)}`
              : 'A repository root for another Claude or team: spec, tasks, open questions and the designs.'}
          </div>
        </div>
        {data.handoff && <a className="btn sm" href={`/files/projects/${project.id}/handoff`} download>Download</a>}
        {canRun && <button className="btn sm" disabled={busy || !data.spec.exists || handoff.isPending} title={data.spec.exists ? '' : 'Write the spec first'}
                           onClick={() => handoff.mutate()}>{data.handoff ? 'Prepare again' : 'Prepare with Claude'}</button>}
      </div>
      {data.changes && <ChangesBlock project={project} changes={data.changes} busy={busy} canRun={canRun} onStarted={refresh} />}
      {data.run && <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)' }}><ClaudeRunPanel run={data.run} canStop={canRun} /></div>}
      {asking && <SpecRunDialog project={project} revise={data.spec.exists} round={data.round?.number} handedOff={!!data.handoff}
                                onClose={() => setAsking(false)} onStarted={refresh} />}
    </section>
  );
}

function SpecRunDialog({ project, revise, round, handedOff, onClose, onStarted }: { project: Project; revise: boolean; round?: number; handedOff: boolean; onClose: () => void; onStarted: () => void }) {
  const toast = useToast();
  const [instructions, setInstructions] = useState('');
  const start = useMutation({
    mutationFn: () => post<ClaudeRun>(`/api/projects/${project.id}/spec/claude`, { instructions }),
    onSuccess: () => { onStarted(); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={revise ? 'Revise the spec with Claude' : 'Write the spec with Claude'} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>
          Claude {revise ? 'revises spec/tech-spec.md in place (requirement IDs stay the same)' : 'writes spec/tech-spec.md'} from v{round}, the notes and decisions.md.
          It usually takes 5 to 15 minutes, and runs in the background: you can leave this page.
        </p>
        {handedOff && <p className="callout warn" style={{ margin: 0 }}>
          This project has been handed off. If the build has started, use a <b>change request</b> instead (under Change requests), so the build team gets only what changed.
        </p>}
        <label className="field"><span>Instructions (optional)</span>
          <textarea className="input" rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000}
                    placeholder={revise ? 'e.g. Add offline support for the scanner, and drop the web admin from phase 1' : 'e.g. Phase 1 is iOS and Android only'} />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={start.isPending}>{start.isPending && <span className="spin" />}Start</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- change requests ----------

function DiffSummary({ diff }: { diff: ScreenDiff }) {
  const list = (items: { id: string; title: string }[]) => items.map((s) => <code key={s.id} title={s.title}>{s.id}</code>).reduce<React.ReactNode[]>((a, x, i) => i ? [...a, ', ', x] : [x], []);
  return (
    <ul className="tiny diff-summary">
      <li><b>{diff.added.length} added</b>{diff.added.length > 0 && <>: {list(diff.added)}</>}</li>
      <li><b>{diff.changed.length} changed</b>{diff.changed.length > 0 && <>: {list(diff.changed)}</>}</li>
      <li><b>{diff.removed.length} removed</b>{diff.removed.length > 0 && <>: {list(diff.removed)}</>}</li>
      <li className="muted">{diff.unchanged} unchanged{diff.shared.length > 0 && <> · {diff.shared.join(', ')} changed too (can affect every screen)</>}</li>
    </ul>
  );
}

function useChangeActions(projectId: string, onDone: () => void) {
  const toast = useToast();
  const qc = useQueryClient();
  const done = () => { onDone(); qc.invalidateQueries({ queryKey: ['change', projectId] }); };
  const fail = (e: Error) => toast(e.message, true);
  return {
    setStatus: useMutation({ mutationFn: ({ n, status, note }: { n: number; status: string; note?: string }) => patch<ChangeDoc>(`/api/projects/${projectId}/changes/${n}`, { status, note: note ?? '' }),
                             onSuccess: done, onError: fail }),
    apply: useMutation({ mutationFn: (n: number) => post<ClaudeRun>(`/api/projects/${projectId}/changes/${n}/apply`), onSuccess: done, onError: fail }),
    pack: useMutation({ mutationFn: (n: number) => post<ClaudeRun>(`/api/projects/${projectId}/changes/${n}/package`), onSuccess: done, onError: fail }),
  };
}

function ChangeActions({ projectId, c, specRound, busy, canRun, onDone, full }: { projectId: string; c: ChangeRequest; specRound: number | null; busy: boolean; canRun: boolean; onDone: () => void; full?: boolean }) {
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [approving, setApproving] = useState(false);
  const a = useChangeActions(projectId, onDone);
  const stale = c.from !== specRound;
  const canEdit = can('delivery.edit');
  return (
    <>
      {c.status === 'draft' && canEdit && <button className="btn sm" disabled={busy} onClick={() => setApproving(true)}>Record approval</button>}
      {c.status === 'draft' && canEdit && full && <button className="btn sm ghost" disabled={busy} onClick={async () => {
        if (await confirm(`Reject CR-${c.number}?`, { body: 'The spec stays as it is. You can set it back to draft later.', ok: 'Reject', danger: true })) a.setStatus.mutate({ n: c.number, status: 'rejected' });
      }}>Reject</button>}
      {(c.status === 'approved' || c.status === 'rejected') && canEdit && full && <button className="btn sm ghost" disabled={busy} onClick={async () => {
        if (await confirm(`Set CR-${c.number} back to draft?`, { body: c.status === 'approved' ? 'The approval is removed, with its row in decisions.md.' : undefined, ok: 'Back to draft' })) a.setStatus.mutate({ n: c.number, status: 'draft' });
      }}>Back to draft</button>}
      {c.status === 'approved' && canRun && <button className="btn sm primary" disabled={busy || stale || a.apply.isPending} onClick={() => a.apply.mutate(c.number)}
        title={stale ? `Drafted against the spec for v${c.from}, but the spec now describes v${specRound}. Draft a new change request.` : 'Claude updates spec/tech-spec.md in place, then the platform packages it'}>Apply to the spec</button>}
      {c.status === 'applied' && c.package && <a className="btn sm" href={`/files/projects/${projectId}/changes/${c.number}.zip`} download>Download package</a>}
      {c.status === 'applied' && canRun && <button className="btn sm ghost" disabled={busy || a.pack.isPending} onClick={() => a.pack.mutate(c.number)}>{c.package ? 'Package again' : 'Package'}</button>}
      {approving && <ApproveDialog n={c.number} onClose={() => setApproving(false)} onApprove={(note) => a.setStatus.mutate({ n: c.number, status: 'approved', note }, { onSuccess: () => setApproving(false) })} pending={a.setStatus.isPending} />}
      {dialog}
    </>
  );
}

function ApproveDialog({ n, onClose, onApprove, pending }: { n: number; onClose: () => void; onApprove: (note: string) => void; pending: boolean }) {
  const [note, setNote] = useState('');
  return (
    <Modal title={`Record the client's approval of CR-${n}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); onApprove(note); }}>
        <p className="small" style={{ margin: 0 }}>Adds a row to decisions.md and locks the text. Then apply it to the spec.</p>
        <label className="field"><span>How it was approved (optional)</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="e.g. by email, 3 November, with the R 48 000 estimate" />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={pending}>{pending && <span className="spin" />}Approved</button>
        </div>
      </form>
    </Modal>
  );
}

function ChangesBlock({ project, changes, busy, canRun, onStarted }: { project: Project; changes: Changes; busy: boolean; canRun: boolean; onStarted: () => void }) {
  const [drafting, setDrafting] = useState(false);
  const open = changes.items.some((c) => c.status === 'draft' || c.status === 'approved');
  return (
    <div>
      <div className="round-row">
        <div className="grow" style={{ minWidth: 180 }}>
          <b className="small">Change requests</b>
          <div className="muted tiny" style={{ marginTop: 4 }}>
            {changes.spec_round ? <>The spec describes v{changes.spec_round}. </> : <>The platform doesn't know which round the spec describes yet. </>}
            {changes.suggested ? <>v{changes.suggested} has changes since{changes.diff && <>: {changes.diff.added.length} screens added, {changes.diff.changed.length} changed, {changes.diff.removed.length} removed</>}.</>
              : <>Design changes as new rounds (Send to Claude), then draft a change request: the spec is updated in place, and the build gets only what changed.</>}
          </div>
        </div>
        {canRun && <button className="btn sm" disabled={busy || !changes.suggested} onClick={() => setDrafting(true)}
                           title={changes.suggested ? (open ? 'Another change request is still open: make sure they don\'t overlap' : '') : 'Design the changes as a new round first'}>Draft change request</button>}
      </div>
      {changes.items.length > 0 && (
        <ul className="change-list">
          {[...changes.items].reverse().map((c) => (
            <li key={c.number} className="round-row">
              <div className="grow" style={{ minWidth: 180 }}>
                <div className="row wrap" style={{ gap: 6 }}>
                  <Link className="small" to={`/projects/${project.id}/changes/${c.number}`}><b>CR-{c.number}</b>: {c.title}</Link>
                  <span className={`badge ${CHANGE_STATUS[c.status].badge}`}>{CHANGE_STATUS[c.status].label}</span>
                </div>
                <div className="muted tiny" style={{ marginTop: 4 }}>
                  v{c.from} → v{c.to}
                  {c.status === 'approved' && c.approved_by && <> · approval recorded by {c.approved_by}{c.note ? ` (${c.note})` : ''} {ago(c.approved_at)}</>}
                  {c.status === 'applied' && <> · applied {ago(c.applied_at)}{c.package && <> · {c.package.name}</>}</>}
                  {c.status === 'draft' && <> · drafted {ago(c.drafted_at)}</>}
                </div>
              </div>
              <Link className="btn sm ghost" to={`/projects/${project.id}/changes/${c.number}`}>Open</Link>
              <ChangeActions projectId={project.id} c={c} specRound={changes.spec_round} busy={busy} canRun={canRun} onDone={onStarted} />
            </li>
          ))}
        </ul>
      )}
      {drafting && <DraftChangeDialog project={project} changes={changes} onClose={() => setDrafting(false)} onStarted={onStarted} />}
    </div>
  );
}

function DraftChangeDialog({ project, changes, onClose, onStarted }: { project: Project; changes: Changes; onClose: () => void; onStarted: () => void }) {
  const toast = useToast();
  const [to, setTo] = useState(changes.suggested!);
  const [instructions, setInstructions] = useState('');
  const { data: diff } = useQuery({ queryKey: ['change-diff', project.id, to], queryFn: () => get<ScreenDiff>(`/api/projects/${project.id}/changes/diff?to=${to}`),
                                    initialData: to === changes.suggested && changes.diff ? changes.diff : undefined });
  const start = useMutation({
    mutationFn: () => post<ClaudeRun>(`/api/projects/${project.id}/changes/claude`, { to, instructions }),
    onSuccess: () => { onStarted(); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`Draft change request CR-${changes.next_number}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>
          Claude compares v{changes.spec_round}, which the spec describes, with the round you pick, and writes spec/changes/CR-{changes.next_number}.md:
          screens and requirements added, changed and removed (by ID), data changes, effort and risk, and build tasks. The spec itself changes only once the client approves.
        </p>
        <label className="field"><span>Changes up to</span>
          <select className="input" value={to} onChange={(e) => setTo(Number(e.target.value))}>
            {changes.rounds.map((r) => <option key={r.number} value={r.number}>v{r.number}{r.visibility === 'approved' ? ' (approved)' : r.visibility === 'ready' ? ' (shared with the client)' : ''}</option>)}
          </select>
        </label>
        {diff && <div><div className="small"><b>v{diff.from} → v{diff.to}</b></div><DiffSummary diff={diff} /></div>}
        <label className="field"><span>Instructions (optional)</span>
          <textarea className="input" rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000}
                    placeholder="e.g. The client wants this live before December: say what can be phased" />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={start.isPending}>{start.isPending && <span className="spin" />}Start</button>
        </div>
      </form>
    </Modal>
  );
}

export function ChangePage() {
  const { id = '', n = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  useLiveTopics('workspace');
  const { data: project } = useQuery({ queryKey: ['project', id], queryFn: () => get<Project>(`/api/projects/${id}`) });
  const { data: doc } = useQuery({ queryKey: ['change', id, n], queryFn: () => get<ChangeDoc>(`/api/projects/${id}/changes/${n}`) });
  const { data: state } = useSpecHandoff(id);
  const [draft, setDraft] = useState<{ text: string; version: string | null } | null>(null);
  const [preview, setPreview] = useState('');
  const editing = draft !== null;
  const dirty = editing && draft.text !== doc?.text;

  useEffect(() => {
    if (project) setCrumbs([{ label: 'Clients', to: '/clients' }, { label: project.client.name, to: `/clients/${project.client.id}?tab=projects` },
                            { label: project.name, to: `/projects/${project.id}` }, { label: `CR-${n}` }]);
  }, [project, n, setCrumbs]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    addEventListener('beforeunload', warn);
    return () => removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => { post<{ html: string }>('/api/preview', { text: draft.text }).then((r) => setPreview(r.html)).catch(() => {}); }, 350);
    return () => clearTimeout(t);
  }, [editing, draft?.text]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { qc.invalidateQueries({ queryKey: ['spec-handoff', id] }); qc.invalidateQueries({ queryKey: ['change', id, n] }); };
  const save = useMutation({
    mutationFn: () => put<ChangeDoc>(`/api/projects/${id}/changes/${n}`, draft),
    onSuccess: (d) => { qc.setQueryData(['change', id, n], d); setDraft(null); toast(`Saved spec/changes/CR-${n}.md`); },
    onError: (e: Error) => toast(e.message, true),
  });
  const busy = active(state?.run) && state?.run?.kind !== 'design-review';
  if (!doc || !project) return <main className="page"><span className="spin muted" /></main>;
  const c = doc.change;
  const s = CHANGE_STATUS[c.status];

  return (
    <Page title={`CR-${c.number}: ${c.title}`} eyebrow={project.name} actions={editing ? <>
      <button className="btn ghost" onClick={() => setDraft(null)}>Cancel</button>
      <button className="btn primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending && <span className="spin" />}Save</button>
    </> : <>
      {can('delivery.edit') && c.status === 'draft' && <button className="btn sm" onClick={() => setDraft({ text: doc.text, version: doc.version })} disabled={busy}
                                                            title={busy ? 'Claude is working on the spec' : ''}>Edit</button>}
      <ChangeActions projectId={id} c={c} specRound={doc.spec_round} busy={busy} canRun={can('claude.run') && can('delivery.edit')} onDone={refresh} full />
    </>}>
      {state?.run && active(state.run) && <div className="card pad" style={{ marginBottom: 16 }}><ClaudeRunPanel run={state.run} canStop={can('claude.run')} /></div>}
      <p className="muted tiny row wrap" style={{ margin: '0 0 10px', gap: 6 }}>
        <span className={`badge ${s.badge}`}>{s.label}</span>
        <span>v{c.from} → v{c.to} · {project.path}/spec/changes/CR-{c.number}.md · updated {ago(doc.updated_at)}</span>
        {c.approved_by && <span>· approval recorded by {c.approved_by}{c.note ? ` (${c.note})` : ''}, {formatDateTime(c.approved_at!)}</span>}
        {c.status === 'approved' && c.from !== doc.spec_round && <span className="badge warn">The spec now describes v{doc.spec_round}: draft a new change request</span>}
      </p>
      {editing ? (
        <div className="spec-editor">
          <textarea className="input mono" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} spellCheck aria-label="Change request (Markdown)" />
          <div className="card pad prose spec-preview" dangerouslySetInnerHTML={{ __html: preview || doc.html }} />
        </div>
      ) : <article className="card pad prose spec-doc" dangerouslySetInnerHTML={{ __html: doc.html }} />}
    </Page>
  );
}

// ---------- the spec page ----------

export function SpecPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  useLiveTopics('workspace');
  const { data: project } = useQuery({ queryKey: ['project', id], queryFn: () => get<Project>(`/api/projects/${id}`) });
  const { data: spec } = useQuery({ queryKey: ['spec', id], queryFn: () => get<SpecDoc>(`/api/projects/${id}/spec`) });
  const { data: state } = useSpecHandoff(id);
  const [draft, setDraft] = useState<{ text: string; version: string | null } | null>(null);
  const [preview, setPreview] = useState('');
  const editing = draft !== null;
  const dirty = editing && draft.text !== spec?.text;

  useEffect(() => {
    if (project) setCrumbs([{ label: 'Clients', to: '/clients' }, { label: project.client.name, to: `/clients/${project.client.id}?tab=projects` },
                            { label: project.name, to: `/projects/${project.id}` }, { label: 'Spec' }]);
  }, [project, setCrumbs]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    addEventListener('beforeunload', warn);
    return () => removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => { post<{ html: string }>('/api/preview', { text: draft.text }).then((r) => setPreview(r.html)).catch(() => {}); }, 350);
    return () => clearTimeout(t);
  }, [editing, draft?.text]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => put<SpecDoc>(`/api/projects/${id}/spec`, draft),
    onSuccess: (doc) => { qc.setQueryData(['spec', id], doc); setDraft(null); toast('Saved spec/tech-spec.md'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const busy = active(state?.run) && state?.run?.kind !== 'design-review';
  if (!spec || !project) return <main className="page"><span className="spin muted" /></main>;

  return (
    <Page title="Tech spec" eyebrow={project.name} actions={editing ? <>
      <button className="btn ghost" onClick={() => setDraft(null)}>Cancel</button>
      <button className="btn primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending && <span className="spin" />}Save</button>
    </> : can('delivery.edit') && spec.exists && (
      <button className="btn" onClick={() => setDraft({ text: spec.text, version: spec.version })} disabled={busy}
              title={busy ? 'Claude is working on the spec' : ''}>Edit</button>
    )}>
      {state?.run && active(state.run) && <div className="card pad" style={{ marginBottom: 16 }}><ClaudeRunPanel run={state.run} canStop={can('claude.run')} /></div>}
      {!spec.exists ? <div className="card empty">No spec yet. Start one from the project page.</div> : editing ? (
        <div className="spec-editor">
          <textarea className="input mono" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} spellCheck aria-label="Spec (Markdown)" />
          <div className="card pad prose spec-preview" dangerouslySetInnerHTML={{ __html: preview || spec.html }} />
        </div>
      ) : (
        <>
          <p className="muted tiny" style={{ margin: '0 0 10px' }}>{project.path}/spec/tech-spec.md · updated {ago(spec.updated_at)}</p>
          <article className="card pad prose spec-doc" dangerouslySetInnerHTML={{ __html: spec.html }} />
        </>
      )}
    </Page>
  );
}
